import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { translate } from "../core/i18n";
import { Accordion, useAccordionButton, Dropdown, FormControl, Tabs, Tab } from "react-bootstrap";
import { AccordionToogle } from "../components/accordions/AccordionToggle";
import { useState, useMemo, useRef, useEffect, forwardRef } from "react";
import { getDomainCfg, store } from "../core/state";
import { getRedirectUrl, redirectTo } from "../features/redirect";
import { ONBOARDING_STATE, PORTAL_VIEW_NAME, QUESTION_MODAL_STATE } from "../core/constants";
import { Onboarding } from "../components/Onboarding";
import { QuestionModal } from "../components/modals/QuestionModal";
import {
  DISCARDED,
  NO_CARDS,
  getHasCodingCards,
  setCurrentJourneyFinished,
  setCurrentOnboardingStep,
  setHasCodingCards,
} from "../features/onboarding";
import { getComponentData } from "../core/views";
import { RoundButton } from "../components/buttons/base/RoundButton";
import { findProjectsWithoutCategory, getNewTemplateProjectId } from "../core/project/projectslist";
import { ToggleThemeButton } from "../components/ThemeContext";
import { executeProjectStarterTask } from "../features/chatrequests";
import { showNewProject, showPromptedProject } from "../core/project/projectdata";
import { LoginRegisterModal } from "../components/modals/LoginRegisterModal";
import { ProjectListTable } from "../components/ProjectListTable";
import { CLOUD_PROJECT_LIST, CLOUD_USER } from "../features/cloudstorage/cloud";
import { loadCurrentProjectList } from "../core/code/loader";
import { ProjectListEntry } from "../core/types";
import ModernHeaderButton from "../components/buttons/base/ModernHeaderButton";

export function PortalView() {
  const [portalState] = store.useState(PORTAL_VIEW_NAME);
  const [appCode, setAppCode] = useState("");
  const [prompt, setPrompt] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchStatus, setSearchStatus] = useState("");
  const [activeKey, setActiveKey] = useState(portalState.cfg?.portalType === 'prompt' ? "templateSelection" : "0");
  const [cloudUser] = store.useState(CLOUD_USER);
  const [cloudProjectList] = store.useState(CLOUD_PROJECT_LIST);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateFilter, setTemplateFilter] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [llmResponse, setLlmResponse] = useState(null);
  const [searchWarning, setSearchWarning] = useState("");

  useEffect(() => {
    if (isDropdownOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const codeAccordionOnClick = useAccordionButton("10", () => {
    setActiveKey("10");
  });
  const onlineAccordionOnClick = useAccordionButton("11", () => {
    setActiveKey("11");
  });
  const projectListAccordionOnClick = useAccordionButton("12", () => {
    setActiveKey("12");
  });

  const viewPortalHeader = portalState.cfg?.viewPortalHeader || translate("view-projects-header", getDomainCfg().product.name);
  const portalType = portalState.cfg?.portalType || "appCode"; // 'appCode' oder 'prompt'
  const portalQuestion = portalState.cfg?.portalQuestion || translate("view-portal-prompt-input");
  const portalPlaceholder = portalState.cfg?.portalPlaceholder || translate("view-portal-prompt-placeholder");
  const portalInputType = portalState.cfg?.portalInputType || "text";
  const buttonStart = portalState.cfg?.buttonStart || translate("view-portal-prompt-action");
  const templateHeader = portalState.cfg?.templateHeader || translate("view-portal-template-header");
  const templateButton = portalState.cfg?.templateButton || translate("view-portal-template-button");
  

  const appCodeChanged = (e: any) => {
    setAppCode(e.target.value);
  };

  const promptChanged = (e: any) => {
    setPrompt(e.target.value);
  };

  const startApp = async (code: string, newProject: boolean = false) => {
    setHasCodingCards(DISCARDED);
    if (llmResponse) {
      await showPromptedProject(llmResponse, prompt);
    } else {
      if (code && newProject) {
        const newProjectId = await getNewTemplateProjectId(code.toLocaleUpperCase());
        console.log("create new project", newProjectId);
        redirectTo(getRedirectUrl(getDomainCfg().viewSettings.editorView, newProjectId));
      } else {
        redirectTo(getRedirectUrl(getDomainCfg().viewSettings.editorView, code));
        
      }
    }
  };

  const exitRequested = () => {
    redirectTo(getRedirectUrl(getDomainCfg().viewSettings.projectsView));
  };

  const createNewProjectRequested = async () => {
    const newProjectId = await getNewTemplateProjectId(getDomainCfg().viewSettings.features.newProjectCode);
    console.log("create new project", newProjectId);
    await startApp(newProjectId);
  };

  const exitButton = (
    <ModernHeaderButton icon="right-to-bracket" onClick={exitRequested} title={translate('exit-btn-title')} text={translate('exit')} variant={'primary'} />
  );

  const themeButton = <ToggleThemeButton />;

  const buttonText = portalState.cfg?.portalType === 'prompt'
    ? buttonStart
    : translate("view-portal-code-action");

  const codeInputButton = (
    <div className="text-center">
      <button
        className="btn btn-outline-orange portal-button"
        id="portalCodeButton"
        type="button"
        data-toggle="collapse"
      >
        {buttonText}
      </button>
    </div>
  );

  const accordion = (
    <Accordion defaultActiveKey="0">
      <AccordionToogle eventKey="1">
        <span className="code-input-text">
          {portalState.cfg?.portalType === 'prompt' 
            ? portalQuestion
            : translate("view-portal-code-input")}
        </span>{" "}
        (
        <span className="fakeLink">
          {portalState.cfg?.portalType === 'prompt'
            ? translate("view-portal-prompt-help")
            : translate("view-portal-code-help")}
          &nbsp;<FontAwesomeIcon icon="question-circle" />
        </span>
        )
      </AccordionToogle>
      <Accordion.Collapse eventKey="1">
        <div
          className="portal-help"
          dangerouslySetInnerHTML={{ __html: portalState.cfg?.onboarding }}
        ></div>
      </Accordion.Collapse>
    </Accordion>
  );

  const appCodeInput = (
    <div className="code-area">
      <div className="code-area-collapsed" aria-labelledby="headingOne">
        {accordion}
        <div id="enter-code">
          <input
            autoFocus
            id="code"
            type="text"
            placeholder="X X X X"
            className="form-control form-control-lg auto-uppercase"
            aria-label="Code"
            aria-describedby="go"
            value={appCode}
            onChange={appCodeChanged}
          />
          <button
            className="btn btncolor-orange w-100 p-3 btn-lg"
            type="button"
            id="go"
            onClick={async () => await startApp(appCode)}
          >
            {translate("view-portal-start-app")}
          </button>
        </div>
      </div>
    </div>
  );
  const renderSearchButtonContent = () => {
    if (isSearching) {
      return (
        <>
          {searchStatus}
          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        </>
      );
    }
    return (
      <>
        {translate("view-portal-search-project")}
        <FontAwesomeIcon icon="magic-wand-sparkles" className="ms-2" />
      </>
    );
  };

  const inputElement = portalInputType === 'multiline' ? (
    <textarea
      autoFocus
      id="prompt"
      placeholder={portalPlaceholder}
      className="form-control form-control-lg"
      aria-label="Prompt"
      aria-describedby="search"
      value={prompt}
      onChange={(e) => {
        promptChanged(e);
        e.target.style.height = 'auto';
        e.target.style.height = e.target.scrollHeight + 'px';
      }}
      style={{ minHeight: '38px', resize: 'none', overflowY: 'hidden' }}
    />
  ) : (
    <input
      autoFocus
      id="prompt"
      type={portalInputType}
      placeholder={portalPlaceholder}
      className="form-control form-control-lg"
      aria-label="Prompt"
      aria-describedby="search"
      value={prompt}
      onChange={promptChanged}
    />
  );

  const promptInput = (
    <div className="prompt-area">
      <div className="prompt-area-collapsed" aria-labelledby="headingOne">
        {accordion}
        <div id="enter-prompt" className="d-flex flex-column">
          <div className="w-100 mb-2">
            {inputElement}
            {searchWarning && (
                <div className="alert alert-warning py-1 px-2 mb-2" role="alert">
                  {searchWarning}
                </div>
              )}
          </div>
          <div className="d-flex justify-content-end">
            <button
              className="btn btncolor-orange"
              type="button"
              id="search"
              onClick={async () => {
                setIsSearching(true);
                setSearchWarning("");
                setSearchStatus(translate("view-portal-searching-project"));
                const llmAnswer = await executeProjectStarterTask(portalState.cfg.llmTask, prompt);
                setIsSearching(false);
                if (llmAnswer.status === "ok" && llmAnswer.result.success) {
                  const selectedProject = portalState.cfg?.templates.find(
                    (template) => template.code === llmAnswer.result.projectId
                  );
                  if (selectedProject) {
                    setSelectedTemplate(selectedProject);
                    setTemplateFilter(selectedProject.title);
                    // Persist the LLM response
                    setLlmResponse({
                      prompt,
                      einschätzung: llmAnswer.result.einschätzung,
                      fragen: llmAnswer.result.fragen,
                      projectName: llmAnswer.result.projectName,
                      projectId: llmAnswer.result.projectId
                    });
                    // Change tab after setting llmResponse
                    setActiveKey("templateSelection");
                  }
                } else if (llmAnswer.status === "ok" && !llmAnswer.result.success) {
                  setSearchStatus(translate("Beschreibe dein Projekt bitte genauer."));
                  setSearchWarning(translate("Beschreibe dein Projekt bitte genauer."));
                } else {
                  setSearchWarning(translate("Fehler beim Ausführen der KI-Einschätzung. Bitte versuche es erneut."));
                }
              }}
            >
              {renderSearchButtonContent()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const onlineActivityText = portalType === 'prompt'
    ? portalState.cfg?.templateHeader
    : translate("view-portal-no-card-text");

  const onlineActivityBtn = (
    <div className="text-center">
      <h4>{onlineActivityText}</h4>
      <button className="btn btn-outline-orange portal-button" type="button">
        {templateButton}
      </button>
    </div>
  );

  const showAppTypes = portalState.cfg?.showAppTypes;
  const showAppDescription = portalState.cfg?.showAppDescription;

  const onlineProjects = portalState.cfg?.templates
    .filter((project: any) => !project.hidden)
    .map(
      (project: any, index: number) => {
        return (
          <div key={project.code + index} className="project-item" title={project.shortDescription || project.title} onClick={ async () => await startApp(project.code)}>
            <div className="project-image-container">
              <img
                src={getDomainCfg().projectSettings.iconPath + project.icon}
                className="project-btn"
                
                width="150"
                height="150"
                alt={project.title}              
              />
              {showAppDescription && project.description && (
                <div className="project-title">{project.title}</div>
              )}
              {showAppTypes && project.type && (
                <div className="project-type">{project.type}</div>
              )}
            </div>
          </div>
        );
      }
    );
  
  const filteredTemplates = useMemo(() => {
    return portalState.cfg?.templates.filter((project: any) =>
      !project.hidden && project.title.toLowerCase().includes(templateFilter.toLowerCase())
    );
  }, [portalState.cfg?.templates, templateFilter]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTemplateFilter(e.target.value);
    //setLlmResponse(null); 
  };

  const handleDropdownClick = () => {
    setIsDropdownOpen(true);
    setTemplateFilter('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const onlineActivities = (
    <div className="activity-area">
      <span>{templateHeader}</span>
      <div
        id="collapseTwo"
        className="text-center"
        aria-labelledby="headingTwo"
      >
        
        {portalType === 'prompt' ? (
          <div className="d-flex flex-column align-items-center">
            <div className="mt-3 w-100" ref={dropdownRef}>
              <div className="position-relative">
                <div className="position-relative w-100" onClick={handleDropdownClick}>                                  
                  <div className="d-flex align-items-center">
                    <div className="position-relative w-100" >
                      <FormControl
                        ref={inputRef}
                        className="w-100 ps-5"
                        placeholder={translate("view-portal-select-template")}
                        onChange={handleInputChange}
                        value={templateFilter}
                        
                      />
                      {selectedTemplate && (
                        <img
                          src={`/assets/img/icons/${selectedTemplate.icon}`}
                          alt={selectedTemplate.title}
                          className="position-absolute"
                          style={{width: '24px', height: '24px', left: '10px', top: '50%', transform: 'translateY(-50%)'}}
                        />
                      )}
                    </div>
                  </div>
                  <div className="position-absolute top-50 end-0 translate-middle-y pe-2">
                    <FontAwesomeIcon icon="caret-down" />
                  </div>
                </div>
                {isDropdownOpen && (
                  <div className="portal-drop-down position-absolute w-100 mt-1 border rounded shadow-sm" style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}>
                    {(filteredTemplates.length < 1 ? portalState.cfg?.templates.filter((project: any) => !project.hidden) : filteredTemplates).map((project: any, index: number) => (
                      <div 
                        key={project.code + index} 
                        className="template-item p-2 cursor-pointer d-flex align-items-center"
                        onClick={() => handleTemplateSelection(project)}
                      >
                        <img src={`/assets/img/icons/${project.icon}`} alt={project.title} className="me-2" style={{width: '24px', height: '24px'}} />
                        <span>{project.title}</span>
                        <span className="ms-auto badge bg-secondary">{project.type}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {selectedTemplate && (<>
              <div className="mt-3 w-100" style={{ border: '1px solid #ccc', padding: '10px', borderRadius: '5px' }}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center">
                    <img src={`/assets/img/icons/${selectedTemplate.icon}`} alt={selectedTemplate.title} style={{width: '32px', height: '32px'}} className="me-2" />
                    <h5 className="mb-0">{selectedTemplate.title}</h5>
                  </div>
                  <span className="badge bg-secondary">{selectedTemplate.type}</span>
                </div>
                <p>{selectedTemplate.shortDescription}</p>
                
                {llmResponse && llmResponse.einschätzung && (<>
                  <hr/>
                  <p><strong>KI-Einschätzung:</strong> {llmResponse.einschätzung}</p>
                  </>
                )}
              <div className="text-end">
                  <button
                    className="btn btncolor-orange"
                    onClick={ async () => await startApp(selectedTemplate.code, true)}
                  >
                    {translate("view-portal-create-project")}
                    <FontAwesomeIcon icon="plus" className="ms-2" />
                  </button>
                </div>  
              </div>
              
              </>
            )}

          </div>
        ) : (
          <div className="online-projects">{onlineProjects}</div>
        )}
      </div>
    </div>
  );

  const newProjectAvailable = getDomainCfg().viewSettings.features.newProjectCode;
  const newProjectButton = (
    <div className="text-center mt-2">
      <h4>{translate("view-portal-new-project-text")}</h4>
      <button
        className="btn btn-outline-orange portal-button"
        type="button"
        onClick={createNewProjectRequested}
      >
        {translate("view-portal-new-project-btn")}
      </button>
    </div>
  );

  let activeEventKey = activeKey;
  if (activeKey === "0") {
    activeEventKey = getHasCodingCards() === NO_CARDS ? "11" : "10";
  }


  const projectCardClicked = async (project: ProjectListEntry) => {
    if (project.id === "NEW") {
      redirectTo(getRedirectUrl(getDomainCfg().viewSettings.portalView));
    } else {
      await startApp(project.id);
    }
  };

  let projectList: ProjectListEntry[] = [];
  if (portalState.cfg?.showProjectList) {
    if (cloudUser.loggedIn && !cloudProjectList.list && !loading) {
      setLoading(true);
    } else if (cloudUser.loggedIn && cloudProjectList.list && loading) {
      setLoading(false);
    }
    projectList = loadCurrentProjectList(cloudProjectList, cloudUser);
    projectList = findProjectsWithoutCategory(projectList);
  }

  const loadingList = (
    <div className="row" id="loading">
      <div className="col-12 text-center">
        <div className="spinner-grow text-warning" role="status">
          <span className="sr-only">{translate("view-launch-loading")}</span>
        </div>
        <p>{translate("view-projects-apps-loading-text")}</p>
      </div>
    </div>
  );

  const projectListTable = (
    <div className="text-center mt-4">
    <h4>{translate("view-portal-project-list")}</h4>
    {loading ? (loadingList) : (
      <>
        <ProjectListTable 
          projects={projectList} 
          onProjectClick={projectCardClicked} 
          listType={portalState.cfg?.listType} 
          projectsShown={5} 
          newProjectAvailable={false} 
        />
        <button 
          className="btn btn-outline-orange portal-button mt-3" 
          type="button"
          onClick={exitRequested}
        >
          {translate("view-portal-show-all-projects")}
        </button>
      </>
    )}
    </div>
  );

  const handleTemplateSelection = (project: any) => {
    setSelectedTemplate(project);
    setTemplateFilter(project.title);
    setIsDropdownOpen(false);
    
    // If there's an existing llmResponse, update its projectId
    if (llmResponse) {
      setLlmResponse({
        ...llmResponse,
        projectId: project.code
      });
    }
  };

  return (
    <div className="container portal" style={{ maxWidth: 700 }}>
      <QuestionModal />
      <Onboarding view={PORTAL_VIEW_NAME} />
      {portalState.cfg?.showProjectList && <LoginRegisterModal />}
      {!portalState.cfg?.hideMenu && (
        <div className="text-end">
          {exitButton}
          { getDomainCfg().viewSettings.features?.showThemeButton && themeButton }
        </div>
      )}
      <div className="rounded-lg shadow p-3 mb-5 container-app-style" id="portal" >
        <p className="h3">
          <img
            src={getDomainCfg().product.logoUrl}
            className="imgcenter productLogo"            
            max-height="57"
            alt={getDomainCfg().product.name + " Logo"}
          />
        </p>
        <p className="text-center h3">
          {viewPortalHeader}
        </p>
        {portalType === "prompt" ? (
          <Tabs defaultActiveKey="templateSelection" activeKey={activeEventKey} onSelect={(k) => setActiveKey(k)} id="portal-tabs">
            <Tab eventKey="templateSelection" className="portal-tab" title={
              <>
                <FontAwesomeIcon icon="file" className="me-2" />
                {translate("view-portal-template-selection")}
              </>
            }>
              {onlineActivities}
            </Tab>
            <Tab eventKey="promptInput" className="portal-tab activity-area" title={
              <>
                <FontAwesomeIcon icon="magic-wand-sparkles" className="me-2" />
                {translate("view-portal-prompt-input")}
              </>
            }>
              {promptInput}
            </Tab>
          </Tabs>
        ) : (
          <Accordion activeKey={activeEventKey}>
            <span onClick={codeAccordionOnClick}>
              {activeEventKey !== "10" && codeInputButton}
            </span>
            <Accordion.Collapse eventKey="10">
              {appCodeInput}
            </Accordion.Collapse>
            <span onClick={onlineAccordionOnClick}>
              {activeEventKey !== "11" && onlineActivityBtn}
            </span>
            <Accordion.Collapse eventKey="11">
              {onlineActivities}
            </Accordion.Collapse>
          </Accordion>
        )}
        {portalState.cfg?.showProjectList && projectList && projectList.length > 0 && projectListTable}

        {newProjectAvailable && newProjectButton}
      </div>
    </div>
  );
}
